import { formatDistance } from 'date-fns'

const DATE_ONLY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/
const MS_PER_DAY = 1000 * 60 * 60 * 24

function getUtcDayStartMs(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

function differenceInUtcCalendarDays(left: Date, right: Date): number {
  return (getUtcDayStartMs(left) - getUtcDayStartMs(right)) / MS_PER_DAY
}

export type StableDateInput = Date | string | number | null | undefined

export type StableDateFormat = 'short' | 'medium' | 'long'

const DATE_FORMAT_OPTIONS: Record<
  StableDateFormat,
  Intl.DateTimeFormatOptions
> = {
  short: {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
    timeZone: 'UTC',
  },
  medium: {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  },
  long: {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  },
}

/**
 * Parse date-only values as UTC calendar dates so the displayed day stays
 * stable across SSR/ISR hydration and browser time zones.
 */
export function parseStableDateInput(input: StableDateInput): Date | null {
  if (input == null) return null

  if (input instanceof Date) {
    return isNaN(input.getTime()) ? null : input
  }

  if (typeof input === 'number') {
    const date = new Date(input)
    return isNaN(date.getTime()) ? null : date
  }

  const dateOnlyMatch = input.match(DATE_ONLY_REGEX)
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch
    return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))
  }

  const parsed = new Date(input)
  return isNaN(parsed.getTime()) ? null : parsed
}

/**
 * Format calendar-style dates in UTC so a YYYY-MM-DD input always renders as
 * the same day regardless of where the page hydrates.
 */
export function formatStableDate(
  input: StableDateInput,
  format: StableDateFormat = 'medium',
): string | null {
  const date = parseStableDateInput(input)
  if (!date) return null

  return new Intl.DateTimeFormat('en-US', DATE_FORMAT_OPTIONS[format]).format(
    date,
  )
}

/**
 * Derive the render-stamped "today" date in UTC for deterministic day-based
 * filters and labels on SSR/ISR pages.
 */
export function getStableIsoDateStringAt(
  referenceMs: number | null,
): string | null {
  if (referenceMs == null) return null
  return new Date(referenceMs).toISOString().slice(0, 10)
}

export function getStableIsoDateStringAtOffset(
  referenceMs: number | null,
  days: number,
): string | null {
  if (referenceMs == null) return null

  const date = new Date(referenceMs)
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

export function getStableYearAt(referenceMs: number | null): number | null {
  if (referenceMs == null) return null
  return new Date(referenceMs).getUTCFullYear()
}

interface FormatRelativeTimeOptions {
  addSuffix?: boolean
  fallback?: 'absolute' | 'blank'
}

export function formatRelativeTimeFromReference(
  input: StableDateInput,
  referenceMs: number | null,
  { addSuffix = true, fallback = 'absolute' }: FormatRelativeTimeOptions = {},
): string | null {
  const date = parseStableDateInput(input)
  if (!date) return null

  if (referenceMs == null) {
    return fallback === 'blank' ? '' : formatStableDate(date)
  }

  return formatDistance(date, new Date(referenceMs), { addSuffix })
}

export function formatRecentDateLabelFromReference(
  input: StableDateInput,
  referenceMs: number | null,
): string | null {
  const date = parseStableDateInput(input)
  if (!date) return null

  if (referenceMs == null) {
    return formatStableDate(date)
  }

  const referenceDate = new Date(referenceMs)
  const dayDiff = differenceInUtcCalendarDays(referenceDate, date)

  if (dayDiff === 0) return 'Today'
  if (dayDiff === 1) return 'Yesterday'

  const distance = formatDistance(date, referenceDate, { addSuffix: true })
  if (distance.includes('day')) return distance

  return formatStableDate(date)
}

export function getDaysRemainingAt(
  input: StableDateInput,
  referenceMs: number,
): number | null {
  const date = parseStableDateInput(input)
  if (!date) return null

  return Math.ceil((date.getTime() - referenceMs) / MS_PER_DAY)
}

export function getTimelineStatusAt(
  input: StableDateInput,
  referenceMs: number,
  isDeadline = false,
): 'past' | 'current' | 'future' {
  const date = parseStableDateInput(input)
  if (!date) return 'future'

  if (isDeadline) {
    const daysUntil = Math.ceil((date.getTime() - referenceMs) / MS_PER_DAY)

    if (daysUntil < 0) return 'past'
    if (daysUntil <= 7) return 'current'
    return 'future'
  }

  return date.getTime() < referenceMs ? 'past' : 'future'
}

export function isPastAt(
  input: StableDateInput,
  referenceMs: number | null,
): boolean {
  const date = parseStableDateInput(input)
  if (!date || referenceMs == null) return false
  return date.getTime() < referenceMs
}
